<script>
import api from "@/api/api"
import TicketLayouts from "@/components/Tickets/layouts/TicketLayouts.vue"
import TicketBoardCardTemplate from "@/components/Tickets/widgets/TicketBoardCardTemplate.vue"
import {mapActions, mapGetters} from "vuex"
import {connectToWs, subscribe, onMessage, sendMessage, closeWs} from "@/services/websocket"
import {statusPriority} from "@/constants/ticket-status"

import {formatFileSize, isImage, getFileIcon, downloadFile} from "@/common/fileUtils"
import {getInitials} from "@/common/stringUtils"
import {mapTicketFromAPI, getActionOptionsForTicket} from "@/common/ticketMapper"
import {mergeTicketsData} from "@/common/kanbanHelpers"
import {PRIORITY_OPTIONS, TICKETS_PER_PAGE, SCROLL_DEBOUNCE_MS, SCROLL_THRESHOLD} from "@/constants/ticketConstants"

const PRIORITY_FILTERS = [
  {value: 'all', text: 'Любой приоритет'},
  {value: 'urgent', text: 'Срочный'},
  {value: 'high', text: 'Высокий'},
  {value: 'medium', text: 'Средний'},
  {value: 'low', text: 'Низкий'},
]

export default {
  name: "TicketBoard",
  components: {TicketLayouts, TicketBoardCardTemplate},

  data() {
    return {
      statuses: [],
      kanbanData: [],
      history: [],
      isLoading: false,
      statusPagination: {},
      loadingStatuses: {},
      scrollDebounceTimers: {},

      search: '',
      priorityFilter: 'all',
      priorityFilters: PRIORITY_FILTERS,

      payload: {
        id: null,
        title: '',
        description: '',
        priority: '',
        action_id: null,
        category: {id: null, name: ''},
        type: {id: null, name: ''},
      },
      isSidebarLoading: false,
      isHistoryTab: false,
      sidebarTicket: null,
      priorityOptions: PRIORITY_OPTIONS,

      messages: [],
      newMessageText: '',
      isSendingMessage: false,
      attachedFiles: [],
      token: localStorage.getItem('token'),
    }
  },

  computed: {
    ...mapGetters('moduleTickets', ['ticketActions', 'categoryOptions', 'typeOptions']),
    ...mapGetters(['getUserId', 'getUserName', 'getUserEmail']),

    userId() {
      return this.getUserId
    },
    userName() {
      return this.getUserName
    },
    userEmail() {
      return this.getUserEmail
    },

    sortedStatuses() {
      const statuses = Array.isArray(this.statuses) ? this.statuses : []
      return statuses.slice().sort((a, b) => {
        const aPriority = statusPriority[a.slug] ?? Number.MAX_SAFE_INTEGER
        const bPriority = statusPriority[b.slug] ?? Number.MAX_SAFE_INTEGER
        return aPriority - bPriority
      })
    },

    hasAnyFilter() {
      return Boolean(this.search.trim()) || this.priorityFilter !== 'all'
    },

    /** Фильтры работают по уже загруженным карточкам — бэк отдаёт страницы по статусу. */
    filteredTickets() {
      const query = this.search.trim().toLowerCase()
      return (this.kanbanData || []).filter(ticket => {
        if (this.priorityFilter !== 'all' && ticket.Priority !== this.priorityFilter) return false
        if (!query) return true
        return [ticket.Id, ticket.Title, ticket.User, ticket.UserEmail]
            .some(field => String(field ?? '').toLowerCase().includes(query))
      })
    },

    ticketsByStatus() {
      const grouped = {}
      this.sortedStatuses.forEach(status => {
        grouped[status.slug] = []
      })
      this.filteredTickets.forEach(ticket => {
        if (!grouped[ticket.Status]) grouped[ticket.Status] = []
        grouped[ticket.Status].push(ticket)
      })
      return grouped
    },

    visibleTotal() {
      return this.filteredTickets.length
    },

    loadedTotal() {
      return (this.kanbanData || []).length
    },

    actionOptions() {
      return getActionOptionsForTicket(this.payload.action_id, this.ticketActions)
    },

    reversedMessages() {
      return this.messages.slice().reverse().map(msg => ({
        ...msg,
        initials: this.getInitials(msg.user && msg.user.username || 'U'),
        isMine: this.isMyMessage(msg)
      }))
    }
  },

  methods: {
    getInitials,
    formatFileSize,
    isImage,
    getFileIcon,
    ...mapActions('moduleTickets', ['getTicketActions', 'getTicketCategories', 'getTicketTypes']),

    // ===== Колонки =====
    columnCount(status) {
      const pagination = this.statusPagination[status.id]
      if (this.hasAnyFilter) return (this.ticketsByStatus[status.slug] || []).length
      return pagination ? pagination.total : (this.ticketsByStatus[status.slug] || []).length
    },

    columnHasMore(status) {
      const pagination = this.statusPagination[status.id]
      return Boolean(pagination && pagination.current_page < pagination.last_page)
    },

    /**
     * Догрузка следующей страницы при подходе к низу колонки.
     * Раньше обработчики вешались на внутренний DOM Syncfusion по таймерам —
     * теперь колонка своя, и скролл приходит обычным событием.
     */
    onColumnScroll(event, status) {
      const element = event.target
      if (this.scrollDebounceTimers[status.id]) clearTimeout(this.scrollDebounceTimers[status.id])

      this.scrollDebounceTimers[status.id] = setTimeout(() => {
        const {scrollTop, scrollHeight, clientHeight} = element
        if (scrollHeight <= clientHeight) return
        if ((scrollTop + clientHeight) / scrollHeight >= SCROLL_THRESHOLD) {
          this.loadMoreTicketsForStatus(status.id)
        }
      }, SCROLL_DEBOUNCE_MS)
    },

    async loadMoreTicketsForStatus(statusId) {
      const pagination = this.statusPagination[statusId]
      if (!pagination) return
      if (this.loadingStatuses[statusId]) return
      if (pagination.current_page >= pagination.last_page) return

      await this.loadTicketsForStatus(statusId, pagination.current_page + 1, false)
    },

    async getListTickets() {
      this.isLoading = true
      try {
        await this.getStatuses()
        await Promise.all(this.statuses.map(status => this.loadTicketsForStatus(status.id, 1, true)))
      } finally {
        this.isLoading = false
      }
    },

    async loadTicketsForStatus(statusId, page = 1, isInitial = false) {
      if (!isInitial) this.$set(this.loadingStatuses, statusId, true)

      try {
        const res = await api.tickets.getListTickets({status: statusId, page, per_page: TICKETS_PER_PAGE})

        const tickets = (res.data?.data || []).map(ticket => mapTicketFromAPI(ticket, this.ticketActions))
        const currentStatusSlug = this.statuses.find(status => status.id === statusId)?.slug

        this.kanbanData = mergeTicketsData(this.kanbanData, tickets, currentStatusSlug, page === 1)

        this.$set(this.statusPagination, statusId, {
          current_page: res.data?.meta?.current_page || page,
          last_page: res.data?.meta?.last_page || 1,
          per_page: res.data?.meta?.per_page || TICKETS_PER_PAGE,
          total: res.data?.meta?.total || 0,
        })
      } catch (e) {
        this.$toast.error('Не удалось загрузить тикеты')
      } finally {
        if (!isInitial) this.$set(this.loadingStatuses, statusId, false)
      }
    },

    async getStatuses() {
      try {
        const res = await api.tickets.getListActionStatuses()
        this.statuses = res.data?.data || []
      } catch (e) {
        this.statuses = []
        this.$toast.error('Не удалось загрузить статусы')
      }
    },

    resetFilters() {
      this.search = ''
      this.priorityFilter = 'all'
    },

    // ===== Сайдбар =====
    async openSidebar(ticketData, mode = 'chat') {
      this.sidebarTicket = ticketData
      this.isHistoryTab = mode === 'history'

      if (ticketData) {
        this.payload = {
          id: ticketData.Id,
          title: ticketData.Title,
          description: ticketData.Summary,
          priority: ticketData.Priority,
          action_id: ticketData.ActionId,
          category: {id: ticketData.CategoryId, name: ticketData.Category},
          type: {id: ticketData.TypeId, name: ticketData.Type},
        }
      }

      this.$root.$emit('bv::toggle::collapse', 'sidebar-right')

      if (!ticketData?.Id) return

      if (mode === 'history') {
        await this.getTicketHistory(ticketData.Id)
      } else {
        this.subscribeToTicketChat(ticketData.Id)
        await this.getTicketMessages(ticketData.Id)
        this.$nextTick(() => this.scrollToBottom())
        const markReadMessageStatus = (await api.tickets.markMessageAsRead(ticketData.Id)).status
        if (markReadMessageStatus === 204) {
          this.patchTicket(ticketData.Id, {UnreadMessageCount: 0})
        }
      }
    },

    handleSidebarClose() {
      this.sidebarTicket = null
      this.isHistoryTab = false
      document.body.style.overflow = 'auto'
    },

    /** Точечное обновление карточки в списке — без перезагрузки всей доски. */
    patchTicket(ticketId, patch) {
      this.kanbanData = this.kanbanData.map(ticket =>
          ticket.Id === ticketId ? {...ticket, ...patch} : ticket
      )
    },

    async onSelectTicketPriority() {
      this.isSidebarLoading = true
      try {
        const {id, priority} = this.payload
        const res = await api.tickets.updateTicket(id, {priority})
        if (res.status === 204) this.patchTicket(id, {Priority: priority})
      } catch (e) {
        this.$toast.error('Ошибка при обновлении тикета')
      } finally {
        this.isSidebarLoading = false
      }
    },

    async onSelectTicketCategory() {
      this.isSidebarLoading = true
      try {
        const {id, category} = this.payload
        const res = await api.tickets.updateTicket(id, {category_id: category.id})
        if (res.status === 204) {
          const categoryName = this.categoryOptions.find(item => item.value === category.id)?.text
          this.patchTicket(id, {Category: categoryName, CategoryId: category.id})
        }
      } catch (e) {
        this.$toast.error('Ошибка при обновлении тикета')
      } finally {
        this.isSidebarLoading = false
      }
    },

    async onSelectTicketType() {
      this.isSidebarLoading = true
      try {
        const {id, type} = this.payload
        const res = await api.tickets.updateTicket(id, {type_id: type.id})
        if (res.status === 204) {
          const typeName = this.typeOptions.find(item => item.value === type.id)?.text
          this.patchTicket(id, {Type: typeName, TypeId: type.id})
        }
      } catch (e) {
        this.$toast.error('Ошибка при обновлении тикета')
      } finally {
        this.isSidebarLoading = false
      }
    },

    async onSelectTicketAction() {
      this.isSidebarLoading = true
      try {
        const {id, action_id} = this.payload
        await api.tickets.updateActionFromTicket(id, action_id, {action_id})
        await this.getListTickets()
      } catch (e) {
        this.$toast.error('Ошибка при обновлении тикета')
      } finally {
        this.isSidebarLoading = false
      }
    },

    async updateTicket() {
      this.isSidebarLoading = true
      try {
        const {id, ...updateData} = this.payload
        await api.tickets.updateTicket(id, updateData)
        this.patchTicket(id, {Title: updateData.title, Summary: updateData.description})
        this.$toast.success('Тикет обновлён')
      } catch (e) {
        this.$toast.error('Ошибка при обновлении тикета')
      } finally {
        this.isSidebarLoading = false
      }
    },

    async getTicketHistory(ticketId) {
      try {
        this.isSidebarLoading = true
        const res = await api.tickets.getTicketHistory(ticketId)
        this.history = res.data.data
      } catch (e) {
        this.$toast.error('Ошибка при получении истории тикета')
      } finally {
        this.isSidebarLoading = false
      }
    },

    async getTicketMessages(ticketId) {
      try {
        this.isSidebarLoading = true
        this.attachedFiles = []
        const res = await api.tickets.getListOfMessages(ticketId)
        this.messages = res.data?.data || []
        this.$nextTick(() => this.scrollToBottom())
      } catch (e) {
        this.$toast.error('Ошибка при получении сообщений')
      } finally {
        this.isSidebarLoading = false
      }
    },

    // ===== Чат =====
    async handleSendMessage() {
      if (!this.newMessageText.trim() && !this.attachedFiles.length) {
        this.$toast.warning('Введите сообщение или прикрепите файл')
        return
      }
      if (this.isSendingMessage) return
      this.isSendingMessage = true

      try {
        const formData = new FormData()
        formData.append('message', this.newMessageText)
        this.attachedFiles.forEach((file) => formData.append('attachments[]', file.raw))

        const res = await api.tickets.sendMessage(this.sidebarTicket.Id, formData)

        this.messages.unshift(res.data.data)
        sendMessage({
          channel: `ticket.${this.sidebarTicket.Id}.messages`,
          event: 'ticket.message.sent',
          data: {
            ticket_id: res.data.data.ticket_id,
            message: res.data.data.message,
            id: res.data.data.id,
            user_id: res.data.data.user_id,
            user: res.data.data.user,
            attachments: res.data.data.attachments,
            created_at: res.data.data.created_at,
          }
        })
        this.newMessageText = ''
        this.attachedFiles = []
        this.$nextTick(() => this.scrollToBottom())
      } catch (e) {
        this.$toast.error('Ошибка отправки сообщения')
      } finally {
        this.isSendingMessage = false
      }
    },

    scrollToBottom() {
      const chatMessages = this.$refs.chatMessages
      if (chatMessages) chatMessages.scrollTop = chatMessages.scrollHeight
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files)
      files.forEach(file => {
        this.attachedFiles.push({
          raw: file,
          name: file.name,
          size: file.size,
          preview: isImage(file.name) ? URL.createObjectURL(file) : null,
        })
      })
      this.$refs.fileInput.value = ''
    },

    removeAttachedFile(index) {
      const file = this.attachedFiles[index]
      if (file.preview) URL.revokeObjectURL(file.preview)
      this.attachedFiles.splice(index, 1)
    },

    isMyMessage(msg) {
      return msg.user?.username === this.userName || msg.user?.email === this.userEmail
    },

    downloadFile(url, filename) {
      downloadFile(url, filename, ({title, message, variant}) => {
        this.$bvToast.toast(message, {title, variant, solid: true})
      })
    },

    // ===== WebSocket =====
    handleMessage(data) {
      switch (data.type || data.event) {
        case 'ticket.message.sent':
          this.handleNewMessage(data)
          break
        case 'ticket_updated':
          this.getListTickets()
          break
      }
    },

    handleNewMessage(data) {
      const message = data.data?.ticket_message || data.data
      if (this.isHistoryTab || !message || this.sidebarTicket?.Id !== message.ticket_id) return

      if (!this.messages.some(item => item.id === message.id)) {
        this.messages.unshift(message)
        this.$nextTick(() => this.scrollToBottom())
      }
    },

    subscribeToTicketChat(ticketId) {
      subscribe(`ticket.${ticketId}.messages`, this.token)
    },

    // ===== Прочее =====
    copyTicketId(id) {
      const text = String(id)
      const done = () => this.$toast.success(`ID ${id} скопирован`)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => {
        })
        return
      }
      const area = document.createElement('textarea')
      area.value = text
      document.body.appendChild(area)
      area.select()
      try {
        document.execCommand('copy')
        done()
      } finally {
        document.body.removeChild(area)
      }
    },

    navigateToUser(userId) {
      if (!userId) return
      const route = this.$router.resolve(`/users/${userId}`)
      window.open(route.href, '_blank')
    },

    formatChatDate(value) {
      if (!value) return ''
      const date = new Date(value)
      return isNaN(date.getTime()) ? '' : date.toLocaleString('ru-RU')
    },
  },

  mounted() {
    this.getListTickets()
    this.getTicketActions()
    this.getTicketCategories()
    this.getTicketTypes()

    connectToWs()
    onMessage(this.handleMessage)
    subscribe(`tickets.${1}`, this.token)
    subscribe(`users.${this.userId}.tickets`, this.token)
    subscribe(`admin-users.${this.userId}.tickets`, this.token)
  },

  beforeDestroy() {
    closeWs()
    Object.values(this.scrollDebounceTimers).forEach(timer => clearTimeout(timer))
    this.attachedFiles.forEach(file => {
      if (file.preview) URL.revokeObjectURL(file.preview)
    })
  },

  watch: {
    sidebarTicket: {
      handler(newVal) {
        document.body.style.overflow = newVal?.Id ? 'hidden' : 'auto'
        const sidebarBody = document.querySelector('.b-sidebar-body')
        if (sidebarBody) sidebarBody.style.overflow = newVal?.Id ? 'hidden' : 'auto'
      },
      deep: true
    },
    isHistoryTab: {
      async handler(newVal) {
        if (newVal === true && this.sidebarTicket?.Id) {
          await this.getTicketHistory(this.sidebarTicket?.Id)
        }
      }
    },
  }
}
</script>

<template>
  <TicketLayouts
      :sidebar-loading="isSidebarLoading"
      sidebar-mode="custom"
      :title="sidebarTicket ? `Тикет #${sidebarTicket.Id}` : 'Тикет'"
      @sidebar-close="handleSidebarClose"
  >
    <!-- ======================= САЙДБАР ТИКЕТА ======================= -->
    <template #sidebar-custom-content>
      <div class="sidebar-content-wrapper">
        <div class="sidebar-top-section">
          <b-form @submit.prevent="updateTicket">
            <label class="tb-label">Тема</label>
            <b-form-input v-model="payload.title" placeholder="Тема обращения"/>

            <label class="tb-label mt-2">Описание</label>
            <b-form-input v-model="payload.description" placeholder="Короткое описание"/>

            <b-button class="mt-2" size="sm" variant="primary" type="submit">
              Сохранить тему и описание
            </b-button>
          </b-form>

          <div class="tb-side-grid">
            <b-form-group label="Приоритет" label-for="ticket-priority">
              <b-form-select id="ticket-priority" v-model="payload.priority"
                             :options="priorityOptions" @change="onSelectTicketPriority"/>
            </b-form-group>
            <b-form-group label="Действие" label-for="ticket-action">
              <b-form-select id="ticket-action" v-model="payload.action_id"
                             :options="actionOptions" @change="onSelectTicketAction"/>
            </b-form-group>
            <b-form-group label="Категория" label-for="ticket-category">
              <b-form-select id="ticket-category" v-model="payload.category.id"
                             :options="categoryOptions" @change="onSelectTicketCategory"/>
            </b-form-group>
            <b-form-group label="Тип" label-for="ticket-type">
              <b-form-select id="ticket-type" v-model="payload.type.id"
                             :options="typeOptions" @change="onSelectTicketType"/>
            </b-form-group>
          </div>

          <div class="tabs w-100 d-flex justify-content-between align-items-center mt-3">
            <b-button @click="isHistoryTab = false" class="w-100 border-none rounded-0"
                      :class="{'active': !isHistoryTab}">
              Чат
            </b-button>
            <b-button @click="isHistoryTab = true" class="w-100 border-none rounded-0"
                      :class="{'active': isHistoryTab}">
              История
            </b-button>
          </div>
        </div>

        <!-- История -->
        <div v-if="isHistoryTab" class="sidebar-scroll-section">
          <div class="history-list mt-3" v-if="history.length">
            <div class="history-item" v-for="(item, index) in history" :key="'h' + index">
              <div class="history-icon">
                <i class="fa fa-arrow-circle-right"></i>
              </div>
              <div class="history-content">
                <div class="history-action">Действие <span>{{ (item.action && item.action.title) || '—' }}</span></div>
                <div class="history-action">Статус <span>{{ (item.action && item.action.status && item.action.status.name) || '—' }}</span></div>
                <div class="history-meta">
                  <span class="history-user">
                    {{ (item.action && item.action.group && item.action.group.group_name) || '—' }} | {{ (item.created_by && item.created_by.username) || 'Unknown' }}
                  </span>
                  <span class="history-date">{{ formatChatDate(item.created_at) }}</span>
                </div>
              </div>
            </div>
          </div>
          <p v-else class="text-center mt-3 text-muted">История пуста</p>
        </div>

        <!-- Чат -->
        <template v-else>
          <div class="chat-messages-section">
            <div class="chat-messages" ref="chatMessages" v-if="reversedMessages.length">
              <div class="message" :class="{ 'message-mine': msg.isMine }"
                   v-for="(msg, index) in reversedMessages" :key="'m' + index">
                <div class="message-avatar" @click="navigateToUser(msg.user && msg.user.id)">
                  {{ msg.initials }}
                </div>
                <div class="message-content">
                  <div class="message-header">
                    <span class="message-author" @click="navigateToUser(msg.user && msg.user.id)">
                      {{ msg.user && msg.user.username || 'Unknown' }}
                    </span>
                    <span class="message-time">{{ formatChatDate(msg.created_at) }}</span>
                  </div>
                  <div class="message-text">{{ msg.message }}</div>

                  <div v-if="msg.attachments && msg.attachments.length" class="message-attachments">
                    <div v-for="(attachment, attIndex) in msg.attachments" :key="'a' + attIndex"
                         class="attachment-item">
                      <div v-if="isImage(attachment.name)" class="attachment-image-wrapper">
                        <img :src="attachment.url" :alt="attachment.name" class="attachment-image"
                             width="200" height="200"
                             @click="downloadFile(attachment.url, attachment.name)"/>
                        <div class="attachment-image-info">
                          <span class="attachment-name">{{ attachment.name }}</span>
                          <span class="attachment-size">{{ formatFileSize(attachment.size) }}</span>
                        </div>
                        <b-button size="sm" variant="light" class="attachment-download-btn"
                                  @click="downloadFile(attachment.url, attachment.name)">
                          <i class="fa fa-download"></i>
                        </b-button>
                      </div>

                      <div v-else class="attachment-file" @click="downloadFile(attachment.url, attachment.name)">
                        <div class="attachment-icon"><i class="fa fa-file-o"></i></div>
                        <div class="attachment-info">
                          <div class="attachment-name">{{ attachment.name }}</div>
                          <div class="attachment-size">{{ formatFileSize(attachment.size) }}</div>
                        </div>
                        <div class="attachment-download"><i class="fa fa-download"></i></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="chat-messages" ref="chatMessages">
              <p class="text-center text-muted">Сообщений пока нет</p>
            </div>
          </div>

          <div class="chat-input-wrapper">
            <div v-if="attachedFiles.length" class="attached-files-preview">
              <div v-for="(file, index) in attachedFiles" :key="'f' + index" class="attached-file-item">
                <img v-if="isImage(file.name)" :src="file.preview" class="attached-file-thumb" alt="preview"/>
                <i v-else class="fa fa-file-o attached-file-icon"></i>
                <span class="attached-file-name" :title="file.name">{{ file.name }}</span>
                <span class="attached-file-size">{{ formatFileSize(file.size) }}</span>
                <b-button variant="link" size="sm" class="attached-file-remove"
                          @click="removeAttachedFile(index)">
                  <i class="fa fa-times"></i>
                </b-button>
              </div>
            </div>

            <div class="chat-input">
              <b-form-textarea v-model="newMessageText" placeholder="Введите сообщение… (Ctrl+Enter — отправить)"
                               rows="1" class="mb-2 w-100" @keydown.ctrl.enter="handleSendMessage"/>
              <input type="file" ref="fileInput" multiple
                     accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                     style="display: none" @change="handleFileSelect"/>
              <b-button variant="outline-secondary" size="sm"
                        class="d-flex justify-content-center align-items-center attach-btn"
                        @click="$refs.fileInput.click()">
                <i class="fa fa-paperclip"></i>
              </b-button>
              <b-button variant="primary" size="sm" block
                        class="d-flex justify-content-center align-items-center"
                        @click="handleSendMessage"
                        :disabled="(!newMessageText.trim() && !attachedFiles.length) || isSendingMessage">
                <i class="fa" :class="isSendingMessage ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
              </b-button>
            </div>
          </div>
        </template>
      </div>
    </template>

    <!-- ======================= ДОСКА ======================= -->
    <div class="tb">
      <div class="tb-toolbar">
        <div class="tb-search">
          <i class="fa fa-search tb-search__icon"></i>
          <input ref="search" type="search" class="tb-search__input" v-model="search"
                 placeholder="ID, тема или пользователь…" @keydown.esc.prevent="search = ''"/>
          <button v-if="search" type="button" class="tb-search__clear" @click="search = ''">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <b-dropdown variant="link" class="tb-drop" no-caret menu-class="tb-drop__menu">
          <template #button-content>
            <span class="tb-drop__btn" :class="{ 'is-on': priorityFilter !== 'all' }">
              <i class="fa fa-flag-o"></i>
              <span>{{ (priorityFilters.find(item => item.value === priorityFilter) || {}).text }}</span>
              <i class="fa fa-angle-down"></i>
            </span>
          </template>
          <b-dropdown-item v-for="option in priorityFilters" :key="'p' + option.value"
                           @click="priorityFilter = option.value">
            <i class="fa fa-check tb-drop__check" :class="{ 'is-hidden': priorityFilter !== option.value }"></i>
            {{ option.text }}
          </b-dropdown-item>
        </b-dropdown>

        <button v-if="hasAnyFilter" type="button" class="tb-chip" @click="resetFilters">
          Показано {{ visibleTotal }} из {{ loadedTotal }} загруженных
          <i class="fa fa-times"></i>
        </button>

        <span class="tb-spacer"></span>

        <button type="button" class="tb-icon-btn" :class="{ 'is-spin': isLoading }"
                @click="getListTickets" v-b-tooltip.hover.bottom="'Обновить доску'">
          <i class="fa fa-refresh"></i>
        </button>

        <router-link :to="{ name: 'tickets_dashboard' }" class="tb-btn-ghost">
          <i class="fa fa-bar-chart"></i> Дашборд
        </router-link>
      </div>

      <!-- скелетон -->
      <div v-if="isLoading" class="tb-board">
        <section v-for="n in 4" :key="'skcol' + n" class="tb-col">
          <div class="tb-col__head">
            <span class="tb-sk tb-sk--title"></span>
          </div>
          <div class="tb-col__body">
            <span v-for="k in 3" :key="'skc' + k" class="tb-sk tb-sk--card"></span>
          </div>
        </section>
      </div>

      <div v-else-if="!sortedStatuses.length" class="tb-empty">
        <i class="fa fa-columns"></i>
        <div class="tb-empty__title">Статусы не настроены</div>
        <div class="tb-empty__text">Добавьте статусы тикетов — из них собираются колонки доски.</div>
        <router-link :to="{ name: 'tickets_status' }" class="tb-btn-ghost">
          <i class="fa fa-cog"></i> Настроить статусы
        </router-link>
      </div>

      <div v-else class="tb-board">
        <section v-for="status in sortedStatuses" :key="'col' + status.id" class="tb-col">
          <header class="tb-col__head" :class="'tb-col__head--' + status.slug">
            <span class="tb-col__dot"></span>
            <span class="tb-col__name" :title="status.name">{{ status.name }}</span>
            <span class="tb-col__count">{{ columnCount(status) }}</span>
          </header>

          <div class="tb-col__body" @scroll.passive="onColumnScroll($event, status)">
            <TicketBoardCardTemplate
                v-for="ticket in ticketsByStatus[status.slug]"
                :key="'t' + ticket.Id"
                :ticket="ticket"
                @open="openSidebar($event, 'chat')"
                @open-history="openSidebar($event, 'history')"
                @open-user="navigateToUser"
                @copy-id="copyTicketId"
            />

            <div v-if="loadingStatuses[status.id]" class="tb-col__more">
              <i class="fa fa-spinner fa-spin"></i> Загружаем карточки…
            </div>

            <div v-else-if="!(ticketsByStatus[status.slug] || []).length" class="tb-col__empty">
              {{ hasAnyFilter ? 'Под фильтр ничего не попало' : 'Тикетов нет' }}
            </div>

            <div v-else-if="columnHasMore(status) && !hasAnyFilter" class="tb-col__hint">
              Прокрутите вниз, чтобы догрузить
            </div>
          </div>
        </section>
      </div>
    </div>
  </TicketLayouts>
</template>

<style scoped lang="scss">
/* ============================ токены доски ============================ */
.tb, .sidebar-content-wrapper {
  --tb-bg: #ffffff;
  --tb-soft: #f6f7fb;
  --tb-soft-2: #eef0f7;
  --tb-border: #e7eaf3;
  --tb-text: #242934;
  --tb-muted: #7b8496;
  --tb-primary: var(--theme-deafult, #7366ff);
  --tb-primary-soft: rgba(115, 102, 255, .1);
  --tb-ok: #2fb344;
  --tb-warn: #f0a020;
  --tb-live: #ff7a1a;
  --tb-danger: #e0414f;
  --tb-info: #3aa1e0;
  --tb-shadow: 0 1px 2px rgba(30, 38, 60, .04);
}

body.dark-only .tb, body.dark-only .sidebar-content-wrapper {
  --tb-bg: #262932;
  --tb-soft: #2f3341;
  --tb-soft-2: #363b4b;
  --tb-border: #374558;
  --tb-text: rgba(255, 255, 255, .87);
  --tb-muted: #98a6ad;
  --tb-primary-soft: rgba(115, 102, 255, .2);
}

.tb {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 0 4px 4px;
}

/* ============================ тулбар ============================ */
.tb-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  background: var(--tb-bg);
  border: 1px solid var(--tb-border);
  border-radius: 14px;
  box-shadow: var(--tb-shadow);
  padding: 10px 12px;
}

.tb-spacer { flex: 1 1 auto; }

.tb-search {
  position: relative;
  flex: 1 1 260px;
  max-width: 380px;

  &__icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--tb-muted);
    font-size: 12px;
    pointer-events: none;
  }

  &__input {
    width: 100%;
    height: 38px;
    padding: 0 34px;
    border: 1px solid var(--tb-border);
    border-radius: 10px;
    background: var(--tb-soft);
    color: var(--tb-text);
    font-size: 13px;
    outline: none;

    &::placeholder { color: var(--tb-muted); }

    &:focus {
      background: var(--tb-bg);
      border-color: var(--tb-primary);
      box-shadow: 0 0 0 3px var(--tb-primary-soft);
    }

    &::-webkit-search-cancel-button { display: none; }
  }

  &__clear {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 22px;
    height: 22px;
    border: 0;
    border-radius: 6px;
    background: var(--tb-soft-2);
    color: var(--tb-muted);
    font-size: 10px;
    cursor: pointer;

    &:hover { background: var(--tb-danger); color: #fff; }
  }
}

.tb-drop {
  ::v-deep > .btn {
    padding: 0;
    border: 0;
    box-shadow: none !important;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 38px;
    padding: 0 12px;
    border: 1px solid var(--tb-border);
    border-radius: 10px;
    background: var(--tb-soft);
    color: var(--tb-text);
    font-size: 13px;
    font-weight: 500;

    &:hover { border-color: var(--tb-primary); }

    &.is-on {
      border-color: var(--tb-primary);
      background: var(--tb-primary-soft);
      color: var(--tb-primary);
    }
  }

  &__check {
    width: 14px;
    margin-right: 4px;
    color: var(--tb-primary);

    &.is-hidden { visibility: hidden; }
  }

  ::v-deep .tb-drop__menu {
    border: 1px solid var(--tb-border);
    border-radius: 12px;
    padding: 6px;

    .dropdown-item {
      border-radius: 8px;
      font-size: 13px;
      padding: 7px 10px;
    }
  }
}

.tb-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 30px;
  padding: 0 11px;
  border: 1px solid var(--tb-warn);
  border-radius: 999px;
  background: rgba(240, 160, 32, .12);
  color: #b57512;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}

.tb-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid var(--tb-border);
  border-radius: 10px;
  background: var(--tb-soft);
  color: var(--tb-muted);
  font-size: 14px;
  cursor: pointer;

  &:hover { border-color: var(--tb-primary); color: var(--tb-primary); }

  &.is-spin i { animation: tb-spin .8s linear infinite; }
}

@keyframes tb-spin { to { transform: rotate(360deg); } }

.tb-btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 14px;
  border: 1px solid var(--tb-border);
  border-radius: 10px;
  background: var(--tb-soft);
  color: var(--tb-text) !important;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none !important;
  cursor: pointer;

  &:hover { border-color: var(--tb-primary); color: var(--tb-primary) !important; }
}

.tb-label {
  display: block;
  margin-bottom: 5px;
  font-size: 12px;
  font-weight: 600;
  color: var(--tb-text);
}

.tb-side-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 12px;
  margin-top: 14px;
}

/* ============================ доска ============================ */
.tb-board {
  display: flex;
  gap: 12px;
  align-items: stretch;
  overflow-x: auto;
  padding-bottom: 6px;
  min-height: 0;
}

.tb-col {
  display: flex;
  flex-direction: column;
  flex: 0 0 300px;
  max-width: 300px;
  background: var(--tb-soft);
  border: 1px solid var(--tb-border);
  border-radius: 14px;
  overflow: hidden;

  &__head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 11px 13px;
    background: var(--tb-bg);
    border-bottom: 1px solid var(--tb-border);
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--tb-muted);
    flex: 0 0 auto;
  }

  &__head--new &__dot,
  &__head--classification &__dot { background: var(--tb-info); }
  &__head--in_progress &__dot { background: var(--tb-live); }
  &__head--waiting_response &__dot,
  &__head--reopened &__dot { background: var(--tb-warn); }
  &__head--resolved &__dot,
  &__head--closed &__dot { background: var(--tb-ok); }
  &__head--rejected &__dot { background: var(--tb-danger); }

  &__name {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 13px;
    font-weight: 600;
    color: var(--tb-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__count {
    padding: 1px 8px;
    border-radius: 999px;
    background: var(--tb-soft-2);
    color: var(--tb-muted);
    font-size: 11px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px;
    overflow-y: auto;
    max-height: calc(100vh - 300px);
    min-height: 200px;

    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: var(--tb-border); border-radius: 3px; }
    &::-webkit-scrollbar-thumb:hover { background: var(--tb-muted); }
  }

  &__more, &__hint, &__empty {
    padding: 12px;
    text-align: center;
    font-size: 12px;
    color: var(--tb-muted);
  }

  &__more {
    border-radius: 10px;
    background: var(--tb-bg);
  }

  &__hint { opacity: .7; font-size: 11px; }
}

.tb-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 60px 20px;
  background: var(--tb-bg);
  border: 1px solid var(--tb-border);
  border-radius: 14px;
  text-align: center;

  > i { font-size: 30px; color: var(--tb-primary); opacity: .6; }

  &__title { font-size: 16px; font-weight: 600; color: var(--tb-text); }
  &__text { font-size: 13px; color: var(--tb-muted); max-width: 420px; }
}

.tb-sk {
  display: block;
  border-radius: 10px;
  background: linear-gradient(90deg, var(--tb-soft) 25%, var(--tb-soft-2) 37%, var(--tb-soft) 63%);
  background-size: 400% 100%;
  animation: tb-shimmer 1.3s ease infinite;

  &--title { height: 14px; width: 60%; }
  &--card { height: 96px; }
}

@keyframes tb-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}


/* ============================ сайдбар и чат ============================ */
.sidebar-header {
  padding-bottom: 15px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 20px;
}

.sidebar-header h5 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.sidebar-header p {
  margin: 5px 0 0 0;
  font-size: 13px;
}

.ticket-id {
  font-size: 0.75rem;
  line-height: 1rem;
  color: rgb(156 163 175);
  background: rgb(229 231 235);
  padding: 4px 8px;
  border-radius: 0.25rem;
}

.tabs button {
  background: transparent !important;
  border: none !important;
  color: black;
}

.tabs button.active {
  border-bottom: 2px solid #667eea !important;
  color: #667eea !important;
}

.history-list {
  overflow-y: auto;
  padding-right: 8px;
}

.history-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 10px;
  background: #f9fafb;
  transition: background 0.2s;
}

.history-item:hover {
  background: #f3f4f6;
}

.history-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #667eea;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.history-content {
  flex: 1;
}

.history-action {
  font-size: 14px;
  font-weight: 400;
  color: #111827;
  margin-bottom: 4px;
}

.history-action span {
  font-weight: 600;
}

.history-meta {
  display: flex;
  gap: 8px;
  font-size: 12px;
  color: #6b7280;
}

.history-user {
  font-weight: 500;
}

.history-date {
  color: #9ca3af;
}

/* Sidebar content wrapper — flex column for full height */
.sidebar-content-wrapper {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 60px);
  overflow: hidden;
}

.sidebar-top-section {
  flex-shrink: 0;
}

.sidebar-scroll-section {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.chat-messages-section {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 10px;
  margin-bottom: 0;
  min-height: 0;
  padding-right: 8px;
}

.message {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}

/* Свои сообщения справа */
.message-mine {
  flex-direction: row-reverse;
}

.message-mine .message-avatar {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.message-mine .message-header {
  flex-direction: row-reverse;
}

.message-mine .message-text {
  color: black;
}

.message-avatar {
  cursor: pointer;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.message-content {
  flex: 1;
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
}

.message-author {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  cursor: pointer;
}

.message-time {
  font-size: 11px;
  color: #9ca3af;
}

.message-text {
  font-size: 13px;
  color: #374151;
  background: #f9fafb;
  padding: 10px 12px;
  border-radius: 8px;
  line-height: 1.5;
}

/* Attachments Styles */
.message-attachments {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.attachment-item {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

/* Image Attachment */
.attachment-image-wrapper {
  position: relative;
  cursor: pointer;
}

.attachment-image {
  width: 100%;
  max-width: 100%;
  height: auto;
  max-height: 300px;
  object-fit: cover;
  display: block;
  transition: opacity 0.2s;
}

.attachment-image:hover {
  opacity: 0.9;
}

.attachment-image-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f9fafb;
  font-size: 12px;
}

.attachment-download-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.9) !important;
  border: none !important;
  padding: 6px 10px;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  opacity: 0;
  transition: opacity 0.2s;
}

.attachment-image-wrapper:hover .attachment-download-btn {
  opacity: 1;
}

/* File Attachment */
.attachment-file {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  cursor: pointer;
  transition: background 0.2s;
}

.attachment-file:hover {
  background: #f9fafb;
}

.attachment-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px;
  flex-shrink: 0;
}

.attachment-info {
  flex: 1;
  min-width: 0;
}

.attachment-name {
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attachment-size {
  font-size: 11px;
  color: #9ca3af;
  display: block;
  margin-top: 2px;
}

.attachment-download {
  color: #667eea;
  flex-shrink: 0;
}

.message-attachments.has-multiple-images {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 8px;
}

.message-attachments.has-multiple-images .attachment-image {
  max-height: 150px;
}

.chat-input-wrapper {
  flex-shrink: 0;
  padding: 10px 0 0 0;
}

.chat-input {
  background: transparent !important;
  border-top: 0 solid transparent;
  padding: 5px 0 0 0;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  height: auto;
  min-height: 63px;
  flex-shrink: 0;
}

.attach-btn {
  width: 40px !important;
  height: 50px;
  flex-shrink: 0;
  border-color: #ced4da;
  color: #6c757d;
}

.attach-btn:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
  color: #495057;
}

.attached-files-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 0;
}

.attached-file-item {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f0f2f5;
  border-radius: 8px;
  padding: 4px 8px;
  max-width: 100%;
}

.attached-file-thumb {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 4px;
}

.attached-file-icon {
  font-size: 18px;
  color: #6c757d;
}

.attached-file-name {
  font-size: 12px;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attached-file-size {
  font-size: 11px;
  color: #999;
}

.attached-file-remove {
  padding: 0 2px;
  color: #999;
  font-size: 14px;
}

.attached-file-remove:hover {
  color: #dc3545;
}

.chat-input textarea {
  height: 50px;
  padding: 12px 15px !important;
  font-size: 14px;
  resize: none;
  flex: 1;
}

.chat-input button {
  width: 40px !important;
  height: 50px;
  flex-shrink: 0;
}

@media screen and (max-width: 630px) {
  .chat-input {
    padding: 12px 0 0 0;
    min-height: 55px;
  }

  .chat-input textarea {
    min-height: 40px;
    height: auto;
    max-height: 120px;
    padding: 10px 12px !important;
  }

  .chat-input button {
    height: 40px;
    min-height: 40px;
  }
}

.history-list::-webkit-scrollbar,
.chat-messages::-webkit-scrollbar {
  width: 6px;
}

.history-list::-webkit-scrollbar-track,
.chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.history-list::-webkit-scrollbar-thumb,
.chat-messages::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.history-list::-webkit-scrollbar-thumb:hover,
.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Responsive для истории */
@media (max-height: 800px) {
  .history-list {
    max-height: calc(100vh - 280px);
  }
}

@media (max-height: 600px) {
  .history-list {
    max-height: calc(100vh - 240px);
    min-height: 200px;
  }
}

@media (max-width: 768px) {
  .history-list {
    max-height: calc(100vh - 320px);
  }

  .history-item {
    padding: 10px;
    margin-bottom: 8px;
  }

  .history-icon {
    width: 28px;
    height: 28px;
  }

  .history-action {
    font-size: 13px;
  }

  .history-meta {
    font-size: 11px;
    flex-direction: column;
    gap: 4px;
  }
}

/* Индикатор загрузки для колонок */
:deep(.column-loading-indicator) {
  display: none;
  align-items: center;
  justify-content: center;
  padding: 15px;
  margin: 10px;
  background: #f9fafb;
  border-radius: 8px;
  font-size: 13px;
  color: #667eea;
  gap: 10px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

:deep(.column-loading-indicator .spinner-border) {
  width: 1.2rem;
  height: 1.2rem;
  border-width: 2px;
}
</style>
